import { Injectable, NgZone } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorService } from '../services/error.service';
import { Router } from '@angular/router';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private errorService: ErrorService,
    private router: Router,
    private ngZone: NgZone
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldSkipErrorHandling(request, error)) {
          return throwError(() => error);
        }

        const statusCode = error.status;

        this.ngZone.run(() => {
          // Handle success status codes (200-300) - don't treat as errors
          if (statusCode >= 200 && statusCode <= 300) {
            return;
          } else if (statusCode >= 400 && statusCode < 500) {
            this.handleClientError(error, statusCode);
          } else if (statusCode >= 500) {
            this.handleServerError(error, statusCode);
          } else {
            this.errorService.handleHttpError(error, statusCode);
          }
        });

        return throwError(() => error);
      })
    );
  }

  private shouldSkipErrorHandling(request: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
    const url = request.url;
    const method = request.method;
    const skipUrls = ['auth/login', 'auth/refresh', 'auth/logout', 'validation/practice-location-provider', 'patients/plans/search', 'patients/comment-categories', 'profile/upload'];
    if (skipUrls.some((skipUrl) => url.includes(skipUrl))) return true;
    // Provider modal + dropdowns: ReferenceDataService maps 5xx to empty fallbacks; avoid duplicate global toast.
    const status = error.status;
    if (this.isSilentReferenceDataGet(request) && (status >= 500 || status === 0)) return true;
    // GET plan follow-up bucket may 404 when no row exists yet; VisitService maps that to empty — avoid global 404 toast.
    if (
      method === 'GET' &&
      url.includes('/follow-up-bucket') &&
      !url.includes('/follow-up-bucket/')
    ) {
      return true;
    }
    // Skip for GET patient document / education document blob (component shows error via ErrorService)
    if (url.includes('patients/') && url.includes('/documents/')) return true;
    if (url.includes('patients/') && url.includes('/education-documents/')) return true;
    // Batch APIs whose errors are shown in popover (Add Batch Admin, Change Batch Number) – do not show on background
    if (url.includes('batches/change-batch')) return true;
    if (url.includes('batches') && (method === 'POST' || method === 'PUT')) return true;
    // Co-Pay modal shows centered alerts above the open popover.
    if (url.includes('payments/copay') && (method === 'POST' || method === 'PUT')) return true;
    return false;
  }

  /**
   * GETs used by Add/Edit Provider (and similar) where the service layer uses catchError and empty defaults.
   * Suppresses duplicate global toasts while the UI still loads without crashing.
   */
  private isSilentReferenceDataGet(request: HttpRequest<unknown>): boolean {
    if (request.method !== 'GET') return false;
    const path = request.url.split('?')[0];
    if (/\/providers\/\d+(\/|$)/.test(path)) {
      return true;
    }
    const lastSlash = path.lastIndexOf('/');
    const segment = lastSlash >= 0 ? path.substring(lastSlash + 1) : path;
    if (segment === 'locations' || segment === 'vendors' || segment === 'insurances') {
      return !/\/providers\/\d+\//.test(path);
    }
    return false;
  }

  private handleClientError(error: HttpErrorResponse, statusCode: number): void {
    switch (statusCode) {
      case 400:
        const validationErrors = this.extractValidationErrors(error);
        if (validationErrors.length > 0) {
          this.errorService.showError(validationErrors.join(', '), statusCode);
        } else {
          this.errorService.handleHttpError(error, statusCode);
        }
        break;

      case 401:
        this.errorService.showError('Your session has expired. Please log in again.', statusCode);
        break;

      case 403:
        this.errorService.showError('You do not have permission to perform this action.', statusCode);
        break;

      case 404:
        this.errorService.handleHttpError(error, statusCode);
        break;

      case 408:
        this.errorService.showError('The request timed out. Please try again.', statusCode);
        break;

      case 409:
        this.errorService.handleHttpError(error, statusCode);
        break;

      case 422:
        const unprocessableErrors = this.extractValidationErrors(error);
        if (unprocessableErrors.length > 0) {
          this.errorService.showError(unprocessableErrors.join(', '), statusCode);
        } else {
          this.errorService.handleHttpError(error, statusCode);
        }
        break;

      case 429:
        this.errorService.showError('Too many requests. Please wait a moment and try again.', statusCode);
        break;

      default:
        this.errorService.handleHttpError(error, statusCode);
    }
  }

  private handleServerError(error: HttpErrorResponse, statusCode: number): void {
    switch (statusCode) {
      case 500:
        this.errorService.showError('A server error occurred. Our team has been notified. Please try again later.', statusCode);
        break;

      case 502:
        this.errorService.showError('Service temporarily unavailable. Please try again later.', statusCode);
        break;

      case 503:
        this.errorService.showError('Service is currently unavailable. Please try again later.', statusCode);
        break;

      case 504:
        this.errorService.showError('The service timed out. Please try again later.', statusCode);
        break;

      default:
        this.errorService.showError('A server error occurred. Please try again later.', statusCode);
    }
  }

  private extractValidationErrors(error: HttpErrorResponse): string[] {
    const errors: string[] = [];

    if (error.error) {
      if (error.error.errors && Array.isArray(error.error.errors)) {
        errors.push(...error.error.errors);
      } else if (error.error.errors && typeof error.error.errors === 'object') {
        Object.values(error.error.errors).forEach((fieldErrors: any) => {
          if (Array.isArray(fieldErrors)) {
            errors.push(...fieldErrors);
          } else {
            errors.push(String(fieldErrors));
          }
        });
      } else if (error.error.message) {
        errors.push(error.error.message);
      } else if (typeof error.error === 'string') {
        errors.push(error.error);
      }
    }

    return errors.length > 0 ? errors : ['Validation failed. Please check your input.'];
  }
}
